
import React, { useState, useCallback } from 'react';
import { AppStep, emptyResume } from './types';
import type { GeminiAnalysisResponse, OptimizedResume } from './types';
import FileUploadScreen from './components/FileUploadScreen';
import LoadingScreen from './components/LoadingScreen';
import ResultsScreen from './components/ResultsScreen';
import { transformResume } from './services/geminiService';
import { GithubIcon } from './components/icons/GithubIcon';

function App() {
  const [step, setStep] = useState<AppStep>(AppStep.Upload);
  const [analysisResult, setAnalysisResult] = useState<GeminiAnalysisResponse | null>(null);
  const [editedResume, setEditedResume] = useState<OptimizedResume>(emptyResume);
  const [error, setError] = useState<string | null>(null);

  const handleFileUpload = useCallback(async (file: File) => {
    setError(null);
    setStep(AppStep.Analyzing);
    try {
      const result = await transformResume(file);
      setAnalysisResult(result);
      setEditedResume(result.optimizedResume);
      setStep(AppStep.Result);
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('An unknown error occurred.');
      }
      setStep(AppStep.Upload);
    }
  }, []);

  const handleResumeChange = useCallback((updatedResume: OptimizedResume) => {
    setEditedResume(updatedResume);
  }, []);

  const handleReset = () => {
    setStep(AppStep.Upload);
    setAnalysisResult(null);
    setEditedResume(emptyResume);
    setError(null);
  };
  
  const renderStep = () => {
    switch (step) {
      case AppStep.Upload:
        return <FileUploadScreen onFileUpload={handleFileUpload} error={error} />;
      case AppStep.Analyzing:
        return <LoadingScreen />;
      case AppStep.Result:
        if (analysisResult) {
          return (
            <ResultsScreen
              analysisResult={analysisResult}
              editedResume={editedResume}
              onResumeChange={handleResumeChange}
              onReset={handleReset}
            />
          );
        }
        // Fallback to upload if result is missing
        handleReset();
        return <FileUploadScreen onFileUpload={handleFileUpload} error={"Something went wrong, please try again."} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen font-sans text-slate-800 flex flex-col">
      <header className="w-full p-4 border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            Global Resume <span className="text-indigo-600">Transformer</span>
          </h1>
          <a href="https://github.com/google/gemini-api-cookbook" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-900 transition-colors">
            <GithubIcon className="w-6 h-6" />
          </a>
        </div>
      </header>
      <main className="flex-grow w-full max-w-7xl mx-auto p-4 md:p-8">
        {renderStep()}
      </main>
    </div>
  );
}

export default App;
